import React from 'react';
import { FileText, Calendar, Mail, Shield, CreditCard, Users, AlertTriangle } from 'lucide-react';

const TermsAndConditions = () => {
  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center mb-4">
          <FileText className="h-12 w-12 text-blue-600 mr-3" />
          <h1 className="text-4xl font-bold text-gray-900">Terms & Conditions</h1>
        </div>
        <div className="flex items-center justify-center text-sm text-gray-600">
          <Calendar className="h-4 w-4 mr-2" />
          <span>Last Updated: October 2025</span>
        </div>
        <p className="mt-4 text-lg text-gray-700 max-w-2xl mx-auto">
          Welcome to BBC Quick List ("the Platform," "we," "our," or "us"). 
          These Terms & Conditions ("Terms") govern your use of our websites, software, products, services, and tools (collectively, the "Services"). 
          By accessing or using the Platform, you agree to be bound by these Terms.
        </p>
      </div>

      {/* Table of Contents */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-blue-900 mb-4">Table of Contents</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <a href="#acceptance" className="text-blue-700 hover:text-blue-900 hover:underline">1. Acceptance of Terms</a>
          <a href="#purpose" className="text-blue-700 hover:text-blue-900 hover:underline">2. Platform Purpose</a>
          <a href="#credits" className="text-blue-700 hover:text-blue-900 hover:underline">3. Credits & Products</a>
          <a href="#pif" className="text-blue-700 hover:text-blue-900 hover:underline">4. Pay-It-Forward (PIF) Vouchers</a>
          <a href="#payments" className="text-blue-700 hover:text-blue-900 hover:underline">5. Non-Custodial Payments</a>
          <a href="#autorepurchase" className="text-blue-700 hover:text-blue-900 hover:underline">6. Auto-Repurchase & Product Access</a>
          <a href="#responsibilities" className="text-blue-700 hover:text-blue-900 hover:underline">7. User Responsibilities</a>
          <a href="#earnings" className="text-blue-700 hover:text-blue-900 hover:underline">8. No Guarantees of Earnings</a>
          <a href="#intellectual" className="text-blue-700 hover:text-blue-900 hover:underline">9. Intellectual Property</a>
          <a href="#termination" className="text-blue-700 hover:text-blue-900 hover:underline">10. Termination</a>
          <a href="#liability" className="text-blue-700 hover:text-blue-900 hover:underline">11. Limitation of Liability</a>
          <a href="#modifications" className="text-blue-700 hover:text-blue-900 hover:underline">12. Modifications</a>
          <a href="#governing" className="text-blue-700 hover:text-blue-900 hover:underline">13. Governing Law</a>
          <a href="#contact" className="text-blue-700 hover:text-blue-900 hover:underline">14. Contact</a>
        </div>
      </div>

      {/* Terms Content */}
      <div className="space-y-8">
        
        {/* Section 1 */}
        <section id="acceptance" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-green-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">1. Acceptance of Terms</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            By creating an account, purchasing credits, using Pay-It-Forward (PIF) vouchers, or participating in any part of the Platform, 
            you confirm that you are <strong>at least 18 years old</strong> and have the legal capacity to enter into these Terms. 
            If you do not agree with these Terms, you must not use the Platform.
          </p>
        </section>

        {/* Section 2 */}
        <section id="purpose" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Users className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">2. Platform Purpose</h2>
          </div>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• BBC Quick List provides marketing tools, lead generation systems, credit-based advertising services, digital products, training resources, and community features.</li>
            <li>• The Platform facilitates <strong>member-to-member transactions</strong> using cryptocurrency payment gateways.</li>
            <li>• We are <strong>not</strong> a financial institution, payment processor, or custodian of user funds.</li>
          </ul>
        </section>

        {/* Section 3 */}
        <section id="credits" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <CreditCard className="h-6 w-6 text-purple-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">3. Credits & Products</h2>
          </div>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• <strong>Credits are digital access tokens</strong> that can be purchased or earned through referrals, vouchers, or promotions.</li>
            <li>• Credits are used to unlock advertising placements, marketplace products, training materials, and other platform features.</li>
            <li>• Credits have <strong>no cash value</strong>, are <strong>non-refundable</strong>, and <strong>non-transferable</strong>, except as explicitly permitted through built-in tools (e.g., PIF system).</li>
          </ul>
        </section>

        {/* Section 4 */}
        <section id="pif" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Users className="h-6 w-6 text-orange-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">4. Pay-It-Forward (PIF) Vouchers</h2>
          </div>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• PIF vouchers enable members to "sponsor" new users into the system.</li>
            <li>• All PIF payments are <strong>peer-to-peer via cryptocurrency</strong> through a third-party payment processor (e.g., NOWPayments).</li>
            <li>• A position in the platform's structure and related product access is activated <strong>only when a voucher is claimed</strong>, not when it's purchased or assigned.</li>
            <li>• Daily and weekly PIF limits may apply, depending on membership tier.</li>
          </ul>
        </section>

        {/* Section 5 */}
        <section id="payments" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <CreditCard className="h-6 w-6 text-green-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">5. Non-Custodial Payments</h2>
          </div>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• All transactions occur <strong>directly between members</strong>.</li>
            <li>• BBC Quick List does <strong>not receive, hold, or manage user funds</strong>.</li>
            <li>• We use third-party payment processors to facilitate routing between member wallets.</li>
            <li>• Users are responsible for ensuring their wallet information is accurate.</li>
          </ul>
        </section>

        {/* Section 6 */}
        <section id="autorepurchase" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">6. Auto-Repurchase & Product Access</h2>
          </div>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• The platform uses <strong>automated payment routing rules</strong> to keep member positions active.</li>
            <li>• In certain product structures, a portion of transactions (e.g., every 5th in Boot Camp) may be routed to the platform's administrative wallet as a service fee.</li>
            <li>• These rules are transparently described in the product documentation and compensation configuration.</li>
          </ul>
        </section>

        {/* Section 7 */}
        <section id="responsibilities" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-yellow-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">7. User Responsibilities</h2>
          </div>
          <p className="text-gray-700 mb-3">You agree to:</p>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• Provide accurate registration information and keep your account secure.</li>
            <li>• Comply with all applicable laws and regulations.</li>
            <li>• Use the Platform for lawful purposes only.</li>
            <li>• Refrain from spamming, fraud, or misrepresentation.</li>
            <li>• Be fully responsible for your communications, content, and transactions with other members.</li>
          </ul>
        </section>

        {/* Section 8 */}
        <section id="earnings" className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
            <h2 className="text-2xl font-bold text-red-900">8. No Guarantees of Earnings</h2>
          </div>
          <ul className="text-red-800 leading-relaxed space-y-2">
            <li>• BBC Quick List provides tools and infrastructure. Any income or results you may achieve depend entirely on your own effort, skills, and activity.</li>
            <li>• We make <strong>no income claims or guarantees</strong>. Past performance is not indicative of future results.</li>
          </ul>
        </section>

        {/* Section 9 */}
        <section id="intellectual" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-purple-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">9. Intellectual Property</h2>
          </div>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• All platform code, branding, text, graphics, and digital materials are owned by BBC Quick List or licensed for use.</li>
            <li>• You may not reproduce, modify, or distribute any platform materials without prior written consent.</li>
          </ul>
        </section>

        {/* Section 10 */}
        <section id="termination" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">10. Termination</h2>
          </div>
          <ul className="text-gray-700 leading-relaxed space-y-2">
            <li>• We reserve the right to suspend or terminate accounts that violate these Terms, engage in fraudulent activity, or misuse the system.</li>
            <li>• Terminated accounts lose access to all features, credits, and placements.</li>
          </ul>
        </section>

        {/* Section 11 */}
        <section id="liability" className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-yellow-600 mr-3" />
            <h2 className="text-2xl font-bold text-yellow-900">11. Limitation of Liability</h2>
          </div>
          <p className="text-yellow-800 mb-3">To the fullest extent permitted by law:</p>
          <ul className="text-yellow-800 leading-relaxed space-y-2">
            <li>• The Platform and its operators shall not be liable for indirect, incidental, or consequential damages.</li>
            <li>• We are not responsible for lost crypto payments, incorrect wallet addresses, or third-party processor failures.</li>
          </ul>
        </section>

        {/* Section 12 */}
        <section id="modifications" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <FileText className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">12. Modifications</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            We may update these Terms at any time. Updates will be posted on this page with a new "Last Updated" date. 
            Continued use of the Platform after changes constitutes acceptance of the new Terms.
          </p>
        </section>

        {/* Section 13 */}
        <section id="governing" className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Shield className="h-6 w-6 text-green-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">13. Governing Law</h2>
          </div>
          <p className="text-gray-700 leading-relaxed">
            These Terms shall be governed by and interpreted in accordance with the laws of the jurisdiction 
            in which the platform operator is registered, without regard to conflict of law principles.
          </p>
        </section>

        {/* Section 14 */}
        <section id="contact" className="bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-center mb-4">
            <Mail className="h-6 w-6 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-blue-900">14. Contact</h2>
          </div>
          <p className="text-blue-800 leading-relaxed">
            For questions or concerns about these Terms, please contact: 
            <a href="mailto:support@bbcquicklist.com" className="font-semibold hover:underline ml-1">
              support@bbcquicklist.com
            </a>
          </p>
        </section>
      </div>

      {/* Footer */}
      <div className="mt-12 pt-8 border-t border-gray-200">
        <div className="bg-gray-50 rounded-lg p-6 text-center">
          <div className="flex items-center justify-center mb-4">
            <FileText className="h-8 w-8 text-gray-600 mr-3" />
            <h3 className="text-xl font-semibold text-gray-900">Legal Documentation</h3>
          </div>
          <p className="text-gray-600 mb-4">
            By using BBC Quick List, you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/privacy-policy" className="text-blue-600 hover:text-blue-800 hover:underline">
              Privacy Policy
            </a>
            <span className="text-gray-400">•</span>
            <a href="/faq" className="text-blue-600 hover:text-blue-800 hover:underline">
              Frequently Asked Questions
            </a>
            <span className="text-gray-400">•</span>
            <a href="mailto:support@bbcquicklist.com" className="text-blue-600 hover:text-blue-800 hover:underline">
              Contact Support
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;